import React from 'react';

import { Helmet } from 'react-helmet';
import { Redirect, useParams } from 'react-router';

import { Box, Container } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import { preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';
import LowInventoryNotifications from '~/components/PriceParrot/LowInventoryNotifications';
import ParityNotifications from '~/components/PriceParrot/ParityNotifications';
import PriceParrotControlCentre from '~/components/PriceParrot/PriceParrotControlCentre';
import PriceParrotParityTool from '~/components/PriceParrot/PriceParrotParityTool';
import PriceParrotScheduler from '~/components/PriceParrot/PriceParrotScheduler';
import PriceParrotShopper from '~/components/PriceParrot/PriceParrotShopper';
import RateDoctor from '~/components/RateDoctor/RateDoctor';

import { createComingSoon } from './ComingSoon';
import MonthlyCampaignPerformancePage from './MonthlyCampaignPerformance/MonthlyCampaignPerformancePage';
import NavTabs from './NavTabs';

const BASE_PATH = '/commercial-ops';
const DEFAULT_SECTION = 'price-parity';

interface TabEntry {
  label: string;
  slug: string;
  component: React.FC;
  keywords?: string;
}

interface SectionWithSubsections {
  label: string;
  slug: string;
  subsections: ReadonlyArray<TabEntry>;
  defaultSubsection: string;
}

interface FlatSection {
  label: string;
  slug: string;
  component: React.FC;
  keywords?: string;
}

type Section = SectionWithSubsections | FlatSection;

function hasSubsections(section: Section): section is SectionWithSubsections {
  return 'subsections' in section;
}

const PRICE_PARITY_SUBSECTIONS: ReadonlyArray<TabEntry> = [
  { label: 'Shopper', slug: 'shopper', component: PriceParrotShopper },
  { label: 'Scheduler', slug: 'scheduler', component: PriceParrotScheduler },
  { label: 'Parity Tool', slug: 'parity-tool', component: PriceParrotParityTool, keywords: 'parity report' },
  { label: 'Control Centre', slug: 'control-centre', component: PriceParrotControlCentre, keywords: 'PPCC' },
];

const VENDOR_NOTIFICATIONS_SUBSECTIONS: ReadonlyArray<TabEntry> = [
  { label: 'Parity Notifications', slug: 'parity-notifications', component: ParityNotifications },
  {
    label: 'Low Inventory Notifications',
    slug: 'low-inventory-notifications',
    component: LowInventoryNotifications,
  },
];

const CONTENT_MANAGEMENT_SUBSECTIONS: ReadonlyArray<TabEntry> = [
  { label: 'Images', slug: 'images', component: createComingSoon('Images') },
  { label: 'Room Content', slug: 'room-content', component: createComingSoon('Room Content') },
];

const SECTIONS: ReadonlyArray<Section> = [
  {
    label: 'Price Parity',
    slug: 'price-parity',
    subsections: PRICE_PARITY_SUBSECTIONS,
    defaultSubsection: 'shopper',
  },
  {
    label: 'Extranet',
    slug: 'extranet',
    component: createComingSoon('Extranet'),
  },
  {
    label: 'Vendor Notifications',
    slug: 'vendor-notifications',
    subsections: VENDOR_NOTIFICATIONS_SUBSECTIONS,
    defaultSubsection: 'parity-notifications',
  },
  {
    label: 'Monthly Performance',
    slug: 'monthly-campaign-performance',
    component: MonthlyCampaignPerformancePage,
    keywords: 'email generator campaign',
  },
  {
    label: 'Content Management',
    slug: 'content-management',
    subsections: CONTENT_MANAGEMENT_SUBSECTIONS,
    defaultSubsection: 'images',
  },
  {
    label: 'Rate Doctor',
    slug: 'rate-doctor',
    component: RateDoctor,
    keywords: 'detect missing rates products derbysoft synxis',
  },
];

// Spotlight: register one entry per leaf (flat section or nested subsection).
// Module-level side effect — requires a static import in App.tsx (not lazy-loaded)
// so that entries are registered before SpotlightProvider renders.
preRegisterSpotlightRoutes(
  SECTIONS.flatMap((section) =>
    hasSubsections(section)
      ? section.subsections.map((sub) => ({
          path: `${BASE_PATH}/${section.slug}/${sub.slug}`,
          label: `Commercial Ops / ${section.label} / ${sub.label}`,
          section: 'Commercial Ops',
          keywords: sub.keywords,
        }))
      : [
          {
            path: `${BASE_PATH}/${section.slug}`,
            label: `Commercial Ops / ${section.label}`,
            section: 'Commercial Ops',
            keywords: section.keywords,
          },
        ],
  ),
);

export default function CommercialOpsPage() {
  const { section, subsection } = useParams<{ section?: string; subsection?: string }>();
  if (!section) {
    return <Redirect to={`${BASE_PATH}/${DEFAULT_SECTION}`} />;
  }

  const currentSection = SECTIONS.find((s) => s.slug === section);
  if (!currentSection) {
    return <Redirect to={`${BASE_PATH}/${DEFAULT_SECTION}`} />;
  }

  let LeafComponent: React.FC;
  let subNav: React.ReactNode = null;

  if (hasSubsections(currentSection)) {
    const activeSub = subsection ? currentSection.subsections.find((s) => s.slug === subsection) : undefined;
    if (!activeSub) {
      return <Redirect to={`${BASE_PATH}/${section}/${currentSection.defaultSubsection}`} />;
    }
    LeafComponent = activeSub.component;
    subNav = (
      <NavTabs
        value={activeSub.slug}
        tabs={currentSection.subsections}
        buildHref={(slug) => `${BASE_PATH}/${section}/${slug}`}
      />
    );
  } else {
    LeafComponent = currentSection.component;
  }

  return (
    <Container maxWidth="xl" sx={{ backgroundColor: 'white' }}>
      <Helmet>
        <title>{`Commercial Ops / ${currentSection.label}`}</title>
      </Helmet>

      <PageHeader title="Commercial Ops" />

      <NavTabs value={currentSection.slug} tabs={SECTIONS} buildHref={(slug) => `${BASE_PATH}/${slug}`} />

      {subNav}

      <Box mt={4}>
        <LeafComponent />
      </Box>
    </Container>
  );
}
